/**
 * Display helper for the SPEC-2300 location field.
 *
 * This file used to carry the lane vocabulary too — labels for `failed_lt` /
 * `lt_connected` / `non_lt`, and a rule for when the lane made the channel
 * redundant. None of it is rendered any more: the lane told an agent nothing
 * the signal chips do not already say, since a live transfer appears there as
 * "live transfer connected" or "live transfer missed", and the default lane
 * means only that no transfer was attempted. `HandoffLane` is still read off
 * the API and carried on the row types; it simply has no display of its own.
 */

import { ChannelOfOrigin, HandoffLocation } from '../types/handoff.types';

/**
 * "Boston, MA" from whichever halves the backend sent. The API drops `location`
 * entirely when both are null, but a partial address (state with no city) still
 * arrives and should render without a dangling comma.
 */
export const formatHandoffLocation = (
  location: HandoffLocation | null | undefined
): string | null => {
  const parts = [location?.city, location?.state].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
};

/**
 * The "Last engaged" wording for a channel, or null to render nothing.
 *
 * SPEC-2727: email handoffs arrive as `email`. Before it was listed here the
 * row rendered blank for them, and any unknown value still does, so a newer
 * backend channel never shows up mislabelled as a call.
 */
export const handoffChannelLabel = (
  channel: ChannelOfOrigin | string | null | undefined
): string | null => {
  if (channel === 'text') return 'via sms';
  if (channel === 'voice') return 'via call';
  if (channel === 'email') return 'via email';
  return null;
};
